/**
 * /subprocessors — public list of third-party service providers that
 * process personal information on behalf of PharmaSync customers.
 *
 * Referenced explicitly by the DPA (Section 7.3) and the Privacy Policy
 * (Section 7) — those documents both depend on this page existing at a
 * stable URL, otherwise their text contains broken references.
 *
 * Keep this list in sync with:
 *   - Legal_Privacy_Draft_v2.md, Section 7
 *   - Legal_DPA_Draft_v2.md, Schedule A
 *   - Internal_Data_Inventory.md, Section 2
 *
 * The "Last updated" date below should be bumped whenever this list
 * changes, and customers subscribed to the DPA should be notified per
 * the change-notice mechanic in DPA Section 7.3 / 7.4.
 */
import { BrandedLayout } from '@/components/BrandedLayout';

export default function Subprocessors() {
  return (
    <BrandedLayout>
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6 prose prose-sm">
        <h1 className="text-2xl font-semibold tracking-tight">
          Sub-processors
        </h1>
        <p className="text-sm text-muted-foreground">
          Last updated: 2026-05-11
        </p>

        <p>
          PharmaSync uses the third-party service providers
          ("sub-processors") listed below to deliver the Service. We have
          written agreements with each requiring confidentiality, security,
          and use of personal information only for the purpose of providing
          services to us.
        </p>

        <h2 className="text-lg font-medium">Current sub-processors</h2>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 font-medium">
                Sub-processor
              </th>
              <th className="text-left py-2 pr-4 font-medium">
                Service provided
              </th>
              <th className="text-left py-2 font-medium">Region</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>Supabase Inc.</strong>
              </td>
              <td className="py-2 pr-4">
                Database, authentication, file storage
              </td>
              <td className="py-2">
                Canada — ca-central-1 (Montreal) primary region
              </td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>Vercel Inc.</strong>
              </td>
              <td className="py-2 pr-4">
                Web application hosting (CDN, serverless functions)
              </td>
              <td className="py-2">
                Global edge network; primary compute in Canada/US
              </td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>Cloudflare Inc.</strong>
              </td>
              <td className="py-2 pr-4">
                DDoS protection, traffic routing, edge caching
              </td>
              <td className="py-2">Global edge network</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>Amazon Web Services, Inc. (Amazon SES)</strong>
              </td>
              <td className="py-2 pr-4">
                Transactional email delivery (sign-in confirmations,
                password resets, staff invites)
              </td>
              <td className="py-2">
                Canada — ca-central-1 (Montreal)
              </td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>Google LLC (Google Workspace)</strong>
              </td>
              <td className="py-2 pr-4">
                Operational email and productivity tools
              </td>
              <td className="py-2">Multi-region</td>
            </tr>
            <tr className="border-b border-border">
              <td className="py-2 pr-4">
                <strong>GoDaddy Operating Co., LLC</strong>
              </td>
              <td className="py-2 pr-4">Domain registration and DNS</td>
              <td className="py-2">
                DNS records only — no personal information
              </td>
            </tr>
            <tr>
              <td className="py-2 pr-4">
                <strong>Stripe Payments Canada Ltd. / Stripe Inc.</strong>
              </td>
              <td className="py-2 pr-4">Payment processing</td>
              <td className="py-2">
                <strong>Cross-border to the United States</strong>
              </td>
            </tr>
          </tbody>
        </table>

        <h2 className="text-lg font-medium">Standby sub-processors</h2>
        <p>
          The following providers are configured but not engaged in normal
          operation. They process personal information only when explicitly
          activated by us, and only for as long as needed to restore the
          primary path.
        </p>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-4 font-medium">
                Sub-processor
              </th>
              <th className="text-left py-2 pr-4 font-medium">
                Service provided
              </th>
              <th className="text-left py-2 font-medium">Region</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2 pr-4">
                <strong>Resend.com, Inc.</strong>
              </td>
              <td className="py-2 pr-4">
                Cold-backup transactional email — engaged manually only
                during a sustained Amazon SES (ca-central-1) regional
                incident. In normal operation, no personal information is
                sent to Resend.
              </td>
              <td className="py-2">
                <strong>Cross-border to the United States</strong>{' '}
                (only while engaged)
              </td>
            </tr>
          </tbody>
        </table>

        <p>
          For details on cross-border transfers (in particular to Stripe in
          the United States and to Resend during an incident), see Section
          7.1 of our <a href="/privacy" className="underline">Privacy
          Policy</a> and Section 8 of our{' '}
          <a href="/dpa" className="underline">Data Processing Agreement</a>.
        </p>

        <h2 className="text-lg font-medium">Notification of changes</h2>
        <p>
          Customers subscribed to our Data Processing Agreement receive at
          least <strong>30 days' advance notice</strong> of any new or
          replacement sub-processor (extended to 60 days, or prior written
          consent, for Pro and Enterprise tiers as specified in the
          relevant Order Form).
        </p>
        <p>
          To receive notifications, email{' '}
          <a
            href="mailto:privacy@pharma-sync.ca"
            className="underline"
          >
            privacy@pharma-sync.ca
          </a>
          .
        </p>

        <h2 className="text-lg font-medium">Questions</h2>
        <p>
          For privacy or sub-processor questions, contact our Privacy
          Officer at{' '}
          <a
            href="mailto:privacy@pharma-sync.ca"
            className="underline"
          >
            privacy@pharma-sync.ca
          </a>
          .
        </p>
      </div>
    </div>
    </BrandedLayout>
  );
}
